import React, { useState, useEffect } from 'react';
import ButtonCommandList from './ButtonCommandList';
import DialoguePointer from './DialoguePointer';
import DialogueChainHelp from './DialogueChainHelp';
import '../Editor.css'; // Import the shared editor styles
import './DialogueChainHelp.css'; // Import the dialogue chain help styles
import './ModeToggleStyles.css'; // Import the new mode toggle styles

const CommandEditor = ({ 
  npcNames = [], 
  dialogues = {}, 
  buttons = {}, 
  commands = {}, 
  setCommands, 
  getPointedDialogues,
  collapsedNpcs = {},
  setCollapsedNpcs,
  findNpcNameByTag = () => null,
  config = { npcType: 'default', sceneTracking: 'scores' },
  entityIds = {}
}) => {
  const [currentCommand, setCurrentCommand] = useState('');
  const [selectedButton, setSelectedButton] = useState(null);
  const [selectedTargetNpc, setSelectedTargetNpc] = useState('');
  const [visibleNpcs, setVisibleNpcs] = useState({});
  const [showNpcFilter, setShowNpcFilter] = useState(false);
  const [collapsedButtonNpcs, setCollapsedButtonNpcs] = useState({});
  const [collapsedDialogueNpcs, setCollapsedDialogueNpcs] = useState({});
  
  // New state for dialogue chain and quiz mode features
  const [quizModeEnabled, setQuizModeEnabled] = useState(false);
  const [dialogueChainEnabled, setDialogueChainEnabled] = useState(false);

  // Helper to determine if we're using script-based tracking
  const isScriptBased = config.sceneTracking === 'scripts';

  // Initialize visible NPCs on first render
  useEffect(() => {
    const initialVisibility = {};
    npcNames.forEach(npc => {
      initialVisibility[npc] = true;
    });
    setVisibleNpcs(initialVisibility);
  }, [npcNames]);

  // Sync the selected button's NPC to be expanded in button list
  useEffect(() => {
    if (selectedButton) {
      // Make sure the selected button's NPC is expanded
      setCollapsedButtonNpcs(prev => ({
        ...prev,
        [selectedButton.npc]: false // force expand
      }));
    }
  }, [selectedButton]);

  const handleCommandChange = (event) => {
    setCurrentCommand(event.target.value);
  };

  const handleTargetNpcSelect = (event) => {
    setSelectedTargetNpc(event.target.value);
  };

  const handleCommandKeyDown = (event) => {
    if (event.key === 'Enter' && selectedButton) {
      setCommands(prevCommands => {
        const newCommands = { ...prevCommands };
        
        // Initialize structure if needed
        if (!newCommands[selectedButton.npc]) {
          newCommands[selectedButton.npc] = {};
        }
        
        if (!newCommands[selectedButton.npc][selectedButton.dialogue]) {
          newCommands[selectedButton.npc][selectedButton.dialogue] = {};
        }
        
        if (!newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button]) {
          newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button] = [];
        }
        
        // Add the new command
        newCommands[selectedButton.npc][selectedButton.dialogue][selectedButton.button].push(currentCommand);
        
        return newCommands;
      });
      setCurrentCommand('');
    }
  };

  const handleButtonSelect = (npc, dialogue, button) => {
    setSelectedButton({ npc, dialogue, button });
  };

  // Toggle dialogue NPC collapse with option to force a state
  const toggleDialogueNpcCollapse = (npc, forceState = null) => {
    if (forceState !== null) {
      // Force a specific state (true = collapsed, false = expanded)
      setCollapsedDialogueNpcs(prev => ({
        ...prev,
        [npc]: forceState
      }));
    } else {
      // Toggle current state
      setCollapsedDialogueNpcs(prev => ({
        ...prev,
        [npc]: !prev[npc]
      }));
    }
  };

  // Collapse all NPCs for button commands
  const collapseAllButtonNpcs = () => {
    const allCollapsed = {};
    npcNames.forEach(npc => {
      allCollapsed[npc] = true;
    });
    setCollapsedButtonNpcs(allCollapsed);
  };

  // Expand all NPCs for button commands
  const expandAllButtonNpcs = () => {
    setCollapsedButtonNpcs({});
  };

  // Collapse all NPCs for dialogue pointers
  const collapseAllDialogueNpcs = () => {
    const allCollapsed = {};
    npcNames.forEach(npc => {
      allCollapsed[npc] = true;
    });
    setCollapsedDialogueNpcs(allCollapsed);
  };

  // Expand all NPCs for dialogue pointers
  const expandAllDialogueNpcs = () => {
    setCollapsedDialogueNpcs({});
  };

  // Toggle NPC visibility
  const toggleNpcVisibility = (npc) => {
    setVisibleNpcs(prev => ({
      ...prev,
      [npc]: !prev[npc]
    }));
  };

  // Toggle all NPCs visibility
  const toggleAllNpcsVisibility = () => {
    const allVisible = Object.values(visibleNpcs).every(v => v);
    const newVisibility = {};
    npcNames.forEach(npc => {
      newVisibility[npc] = !allVisible;
    });
    setVisibleNpcs(newVisibility);
  };

  // Get visible NPCs
  const getVisibleNpcs = () => {
    return npcNames.filter(npc => visibleNpcs[npc]);
  };

  return (
    <div className="command-editor-container">
      <div className="command-input-container">
        <div className="command-input-top">
          <div className="command-input-group">
            <input 
              type="text" 
              value={currentCommand} 
              onChange={handleCommandChange} 
              onKeyDown={handleCommandKeyDown} 
              placeholder={isScriptBased ? "Enter command (script events will be auto-generated for dialogue changes)" : "Enter command"}
              className="editor-input command-input"
              disabled={!selectedButton}
            />
            <select 
              onChange={handleTargetNpcSelect} 
              className="editor-select npc-filter-select"
            >
              <option value="">All NPCs</option>
              {npcNames.map((name, index) => (
                <option key={index} value={name}>
                  {name} {config.npcType === 'custom' && entityIds[name] ? `(${entityIds[name]})` : ''}
                </option>
              ))}
            </select>
            <button 
              className="button-19 filter-toggle"
              onClick={() => setShowNpcFilter(!showNpcFilter)}
              style={{ fontSize: '10px', padding: '8px 12px' }}
            >
              {showNpcFilter ? 'Hide NPC Filter' : 'Show NPC Filter'}
            </button>
          </div>
          
          <DialogueChainHelp />
          
          {/* New feature: Mode toggles */}
          <div className="mode-toggles">
            <label className="mode-toggle-item">
              <input 
                type="checkbox"
                checked={dialogueChainEnabled}
                onChange={() => setDialogueChainEnabled(prev => !prev)}
              />
              <span>Activate Dialogue Chain</span>
            </label>
            <label className="mode-toggle-item">
              <input 
                type="checkbox"
                checked={quizModeEnabled}
                onChange={() => setQuizModeEnabled(prev => !prev)}
              />
              <span>Activate Quiz Mode</span>
            </label>
          </div>
        </div>
        
        {showNpcFilter && (
          <div className="npc-filter-container">
            <div className="npc-filter-header">
              <h4>Filter NPCs</h4>
              <button 
                className="button-19 toggle-all"
                onClick={toggleAllNpcsVisibility}
              >
                {Object.values(visibleNpcs).every(v => v) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="npc-filter-list">
              {npcNames.map((npc, index) => (
                <label key={index} className="npc-filter-item">
                  <input 
                    type="checkbox"
                    checked={visibleNpcs[npc] || false}
                    onChange={() => toggleNpcVisibility(npc)}
                  />
                  <span>
                    {npc} {config.npcType === 'custom' && entityIds[npc] ? `(${entityIds[npc]})` : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        
        {selectedButton && (
          <div className="selected-button-info">
            Currently adding commands to: 
            <span className="selected-npc">{selectedButton.npc}</span> → 
            <span className="selected-dialogue">"{selectedButton.dialogue.substring(0, 20)}..."</span> → 
            <span className="selected-button">"{selectedButton.button}"</span>
            <button 
              className="editor-button clear-selection"
              onClick={() => setSelectedButton(null)}
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>
      
      <div className="command-editors-container" style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
        <div className="editor-section" style={{ width: 'calc(50% - 10px)', flex: '0 0 calc(50% - 10px)' }}>
          <div className="editor-section-header">
            <h3>Buttons and Commands</h3>
            <div className="editor-section-actions">
              <button 
                className="button-19 collapse-all"
                onClick={collapseAllButtonNpcs}
                style={{ fontSize: '10px', padding: '6px 10px' }}
              >
                Collapse All
              </button>
              <button 
                className="button-19 expand-all"
                onClick={expandAllButtonNpcs}
                style={{ fontSize: '10px', padding: '6px 10px' }}
              >
                Expand All
              </button>
            </div>
          </div>
          <ButtonCommandList 
            buttons={buttons}
            commands={commands}
            selectedButton={selectedButton}
            handleButtonSelect={handleButtonSelect}
            visibleNpcs={visibleNpcs}
            collapsedNpcs={collapsedButtonNpcs}
            setCollapsedNpcs={setCollapsedButtonNpcs}
          />
        </div>
        
        <div className="editor-section" style={{ width: 'calc(50% - 10px)', flex: '0 0 calc(50% - 10px)' }}>
          <div className="editor-section-header">
            <h3>Available Dialogues to Point To</h3>
            <div className="editor-section-actions">
              <button 
                className="button-19 collapse-all"
                onClick={collapseAllDialogueNpcs}
                style={{ fontSize: '10px', padding: '6px 10px' }}
              >
                Collapse All
              </button>
              <button 
                className="button-19 expand-all"
                onClick={expandAllDialogueNpcs}
                style={{ fontSize: '10px', padding: '6px 10px' }}
              >
                Expand All
              </button>
            </div>
          </div>
          <DialoguePointer 
            npcNames={getVisibleNpcs()}
            dialogues={dialogues}
            buttons={buttons}
            selectedButton={selectedButton}
            selectedTargetNpc={selectedTargetNpc}
            collapsedNpcs={collapsedDialogueNpcs}
            toggleNpcCollapse={toggleDialogueNpcCollapse}
            getPointedDialogues={getPointedDialogues || (() => ({}))}
            setCommands={setCommands}
            setSelectedButton={setSelectedButton}
            findNpcNameByTag={findNpcNameByTag}
            quizModeEnabled={quizModeEnabled}
            dialogueChainEnabled={dialogueChainEnabled}
            config={config}
            entityIds={entityIds}
          />
        </div>
      </div>
    </div>
  );
};

export default CommandEditor;